import { useState, type ReactNode } from 'react';
import type { AnswerDecisionBody, IdentityMoreProsePayload } from '@ni/shared';
import { HELD_LIST_MAX, exceedsHeldList } from '@ni/shared';
import { useResuming } from '../../api/app-context';
import { useAnswerDecision, useDecision } from '../../api/hooks';
import { standingAnswerLine, standingAnswerOf, loadErrorLine } from '../../api/errors';
import { CandidateCard } from './candidate-card';
import { CandidateList } from './candidate-list';
import { CollapsedBin } from '../ui/collapsed-bin';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import {
  HELD_CANDIDATES_HEAD,
  SAME_PERSON_ASK,
  moreProseHeadline,
  SELECT_CANDIDATE,
  SELECT_CANDIDATE_WORKING,
  SET_ASIDE_BIN_OPEN_LABEL,
  setAsideBinLabel,
  setAsideReason,
  WHAT_YOU_KNOW,
} from '../../lib/copy';
import { Loading } from '../ui/loading';
import { EmptyLine } from '../ui/empty-line';
import { Field } from '../ui/field';
import { type Corrections, SamePersonFoot, SamePersonOpen, useSamePerson } from './same-person';
import './more-prose.css';

/**
 * The more-prose prompt (ui-design §4.5). The branch is never inferred: the
 * payload's `origin` names it, and the discriminating-anchor hint renders
 * verbatim, degrading to the connective line alone when it is null.
 *
 * **Two ways out since the ask tiers** (2026-09-03). Prose is still the answer
 * the question is about — one textarea, no fields, matching intake — but on
 * `over_five` the six-to-fifteen names it is holding are *bindable*, and they
 * are shown expanded as the pick list's own cards, near-match questions and all.
 * A held list the office has to open before it can act is a list that costs a
 * click to discover and reads as a receipt; the whole point of this tier is that
 * the answer may well be on it.
 *
 * The textarea stays **above** the list on purpose. The question is "tell us
 * more", and the shortcut is the shorter road when it happens to be there — not
 * the other way around.
 *
 * `over_fifteen` shows no list at all: the count is in the headline and prose is
 * the only way through, which is also what the server enforces. `zero` and
 * `none_of_these` are unchanged — they have nothing to hold.
 *
 * The held list has the pick list's same-person mode too (2026-10-12,
 * `same-person.tsx`): one person split across two held cards binds as one.
 */

/**
 * What is in flight: the prose box, or one held row. A discriminated value
 * rather than a string keyspace, so a candidate whose ref happened to be spelled
 * like the prose box's key cannot put the wrong button into its working state.
 */
type Pending =
  | { kind: 'prose' }
  | { kind: 'candidate'; ref: string }
  /** `group` is the first ref of a linked group sent by its own button; absent from the mode's foot. */
  | { kind: 'same'; group?: string }
  | null;

const isPending = (pending: Pending, ref?: string): boolean =>
  ref === undefined
    ? pending?.kind === 'prose'
    : pending?.kind === 'candidate' && pending.ref === ref;

export function MoreProseForm({
  payload,
  prose,
  onProseChange,
  onSubmitProse,
  onSelectCandidate,
  onSelectSamePerson,
  pending,
  notice,
}: {
  payload: IdentityMoreProsePayload;
  prose: string;
  onProseChange: (value: string) => void;
  onSubmitProse: () => void;
  onSelectCandidate: (
    candidateRef: string,
    corrections: { original: string; corrected: string }[],
  ) => void;
  /** Two or more held cards the office checked as one person. */
  onSelectSamePerson: (candidateRefs: string[], corrections: Corrections, group?: string) => void;
  pending: Pending;
  /** The standing answer or the error line — the container owns both. */
  notice?: ReactNode;
}) {
  const busy = pending !== null;
  const samePerson = useSamePerson();

  /* **The ceiling is derived here too**, off the same shared rule the server
     projects and answers by. The projection should already have emptied the list
     and relabelled the origin, so this is the third reader agreeing rather than
     the first one deciding — but a page that rendered twenty pressable rows
     against a server that refuses every one of them is the failure worth two
     lines of defence. */
  const capped = exceedsHeldList(
    payload.origin,
    payload.candidate_count,
    payload.retained_candidates.length,
  );
  const held = capped ? [] : payload.retained_candidates.slice(0, HELD_LIST_MAX);
  const headline = moreProseHeadline(
    capped ? 'over_fifteen' : payload.origin,
    Math.max(payload.candidate_count, payload.retained_candidates.length),
  );

  return (
    <div className="run-under">
      <div className="note-form">
        <div className="headline">{headline}</div>
        {payload.discriminating_anchor_hint ? (
          <div className="hint-line">{payload.discriminating_anchor_hint}</div>
        ) : null}

        <Field label={WHAT_YOU_KNOW} htmlFor="more-prose">
          <textarea
            id="more-prose"
            rows={4}
            placeholder="A city, an employer, who knows them"
            value={prose}
            onChange={(event) => onProseChange(event.target.value)}
            disabled={busy}
          />
        </Field>

        <div className="note-actions">
          <WorkingButton
            label="Submit"
            workingLabel="Sending…"
            working={isPending(pending)}
            disabled={prose.trim().length === 0 || (busy && !isPending(pending))}
            onClick={onSubmitProse}
          />
        </div>

        {notice}

        {held.length > 0 ? (
          <div className="held-candidates">
            <div className="held-head">{HELD_CANDIDATES_HEAD}</div>
            {samePerson.checked ? <div className="src">{SAME_PERSON_ASK}</div> : null}
            <CandidateList
              candidates={held}
              card={(candidate, onCorrectionsChange) => (
                <CandidateCard
                  candidate={candidate}
                  label={SELECT_CANDIDATE}
                  workingLabel={SELECT_CANDIDATE_WORKING}
                  working={isPending(pending, candidate.candidate_ref)}
                  disabled={busy && !isPending(pending, candidate.candidate_ref)}
                  onConfirm={(corrections) =>
                    onSelectCandidate(candidate.candidate_ref, corrections)
                  }
                  selection={samePerson.selectionFor(candidate.candidate_ref)}
                  onCorrectionsChange={onCorrectionsChange}
                />
              )}
              onConfirmGroup={(refs, corrections) => onSelectSamePerson(refs, corrections, refs[0])}
              workingGroup={pending?.kind === 'same' ? (pending.group ?? null) : null}
              disabled={busy}
              selecting={samePerson.checked !== null}
            />
            {samePerson.checked ? (
              <SamePersonFoot
                checked={samePerson.checked}
                total={held.length}
                working={pending?.kind === 'same' && pending.group === undefined}
                onCancel={samePerson.close}
                onConfirm={() => {
                  const { refs, corrections } = samePerson.answer(held.map((c) => c.candidate_ref));
                  onSelectSamePerson(refs, corrections);
                }}
              />
            ) : held.length > 1 ? (
              <div className="layer-actions candidate-actions">
                <SamePersonOpen disabled={busy} onClick={samePerson.open} />
              </div>
            ) : null}
          </div>
        ) : null}

        {payload.suppressed.length > 0 ? (
          <CollapsedBin
            label={setAsideBinLabel(payload.suppressed.length)}
            openLabel={SET_ASIDE_BIN_OPEN_LABEL}
          >
            {payload.suppressed.map((entry) => (
              <div className="bin-row" key={entry.candidate_ref}>
                <div className="bname">{entry.name}</div>
                <div className="bdetail">{setAsideReason(entry)}</div>
              </div>
            ))}
          </CollapsedBin>
        ) : null}
      </div>
    </div>
  );
}

export function MoreProsePrompt({
  subjectId,
  decisionId,
  runId,
  runState,
}: {
  subjectId: string;
  decisionId: string;
  runId: string;
  runState: 'awaiting_prose';
}) {
  const decision = useDecision(decisionId);
  const answer = useAnswerDecision(subjectId);
  const { markResuming } = useResuming();
  const [prose, setProse] = useState('');
  const [pending, setPending] = useState<Pending>(null);

  if (decision.isPending)
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  if (decision.isError) return <EmptyLine>{loadErrorLine(decision.error)}</EmptyLine>;
  if (decision.data.kind !== 'identity_more_prose') return null;

  const standing = standingAnswerOf(answer.error);

  /* One mutation for both answers, and one `pending` value across them: the two
     are answers to the *same* row, so the second to arrive loses to the latch
     (409 `DECISION_ALREADY_ANSWERED`, rendered in place). Disabling the rest of
     the form while one is in flight is what keeps the office from racing
     themselves into that. */
  const submit = (next: NonNullable<Pending>, body: AnswerDecisionBody) => {
    setPending(next);
    answer.mutate(
      { decisionId, body, runId },
      {
        onSuccess: () => markResuming(runId, runState),
        onSettled: () => setPending(null),
      },
    );
  };

  return (
    <MoreProseForm
      payload={decision.data.payload}
      prose={prose}
      onProseChange={setProse}
      onSubmitProse={() => submit({ kind: 'prose' }, { prose: prose.trim() })}
      onSelectCandidate={(candidateRef, corrections) =>
        submit(
          { kind: 'candidate', ref: candidateRef },
          {
            candidate_ref: candidateRef,
            // Omitted rather than sent empty, exactly as the pick list omits it.
            ...(corrections.length > 0 ? { anchor_corrections: corrections } : {}),
          },
        )
      }
      onSelectSamePerson={(candidateRefs, corrections, group) =>
        submit(group === undefined ? { kind: 'same' } : { kind: 'same', group }, {
          candidate_refs: candidateRefs,
          ...(corrections.length > 0 ? { anchor_corrections: corrections } : {}),
        })
      }
      pending={pending}
      notice={
        standing ? (
          <div className="src">{standingAnswerLine(standing)}</div>
        ) : answer.isError ? (
          <ErrorLine error={answer.error} />
        ) : null
      }
    />
  );
}
